import { useRequestPasswordResetForm } from '@/forms/request-password-reset.form';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContainer } from '../components/auth-container.component';

const inputSx = {
  '& .MuiFilledInput-root': {
    bgcolor: 'white',
    borderRadius: 2,
    '&:before, &:after': { display: 'none' },
    '&:hover': { bgcolor: 'white' },
    '&.Mui-focused': { bgcolor: 'white' },
  },
};

export function AuthForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email');
  const [submitted, setSubmitted] = useState(false);

  const { register, onSubmit, errors, isPending, setValue, getValues } =
    useRequestPasswordResetForm({
      onSuccess: () => setSubmitted(true),
      // Backend never reveals whether the account exists; a transport error
      // must not either — show the same confirmation regardless.
      onError: () => setSubmitted(true),
    });

  useEffect(() => {
    if (prefillEmail) {
      setValue('email', prefillEmail);
    }
  }, [prefillEmail, setValue]);

  const handleBackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const email = getValues('email');
    navigate(email ? `/auth/login?email=${encodeURIComponent(email)}` : '/auth');
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          color: '#02344C',
          fontWeight: 700,
          mb: 0.75,
          textAlign: 'center',
          letterSpacing: '-0.01em',
        }}
      >
        Passwort vergessen
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 1,
          textAlign: 'center',
          maxWidth: 340,
          lineHeight: 1.6,
        }}
      >
        {submitted
          ? 'Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen einen Link zum Zurücksetzen Ihres Passworts gesendet.'
          : 'Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link, mit dem Sie ein neues Passwort vergeben können.'}
      </Typography>

      {submitted ? (
        <AuthContainer>
          <Button
            onClick={() => navigate('/auth')}
            fullWidth
            variant="contained"
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Zurück zur Anmeldung
          </Button>
        </AuthContainer>
      ) : (
        <AuthContainer component="form" onSubmit={onSubmit}>
          <TextField
            id="email"
            label="E-Mail Adresse"
            type="email"
            variant="filled"
            fullWidth
            required
            autoFocus
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={inputSx}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              width: '100%',
              gap: 1.5,
              mt: 1,
            }}
          >
            <Button
              onClick={handleBackClick}
              fullWidth
              variant="outlined"
              disabled={isPending}
              sx={{
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Zurück
            </Button>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isPending}
              sx={{
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              Link senden{' '}
              {isPending && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Button>
          </Box>
        </AuthContainer>
      )}
    </>
  );
}
